<script setup lang='ts'>
import { LOAD_STATUS } from '@qcwp/common'
import Brand from './brand.vue'
import Series from './series.vue'
import Model from './model.vue'
import type { CascaderSelectedType } from './hook'
import { useCascaderComponent } from './hook'

const props = withDefaults(defineProps<{
  to?: string
  placement?: 'top' | 'right' | 'bottom' | 'left'
  step?: 1 | 2 | 3
  selectedClear?: boolean
  brandId?: string
  seriesId?: string
  modelId?: string
  canCustomize?: boolean
  customizeModelName?: string
  // 是否懒加载数据，如果为true，则不会在组件初始化时加载数据，而是在点击时才加载数据
  lazy?: boolean
}>(), {
  placement: 'bottom',
  selectedClear: false,
  step: 3,
  canCustomize: false,
  customizeModelName: '',
  lazy: false,
})
const emits = defineEmits({
  selected: (data: CascaderSelectedType) => true,
})

const {
  currentStep,
  isSelect,
  state,
  initStatus,
  selectedBrand,
  selectedSeries,
  selectedModel,

  selectBrand,
  selectSeries,
  selectModel,

  clear,
  changeStep,
  initHandle,
} = useCascaderComponent(props, emits)

onDeactivated(() => {
  isSelect.value = false
})
</script>

<template>
  <div relative @click="(isSelect = !isSelect)">
    <slot :state="state">
      <div class="border-1 border-gray-600 flex items-center px-2 py-1 relative cursor-pointer">
        <div>
          <span>选择品牌</span>
        </div>
        <span class="absolute right-2">
          <i i-ic:outline-keyboard-arrow-down />
        </span>
      </div>
    </slot>
    <n-drawer
      v-model:show="isSelect"
      height="80%"
      width="500px"
      :placement="placement"
      :trap-focus="false"
      :block-scroll="false"
      :to="to"
    >
      <n-drawer-content closable>
        <template #header>
          <span text-4 class="cursor-pointer" @click="changeStep(0)">{{ selectedBrand?.brandName || '选择品牌' }}</span>
          <template v-if="[1, 2].includes(currentStep) || selectedBrand?.brandName">
            <i i-ic:baseline-keyboard-arrow-right text-gray-400 />
            <span class="cursor-pointer" text-4 @click="changeStep(1)">{{ selectedSeries?.carSeriesName || '选择车系' }}</span>
          </template>
          <template v-if="(currentStep === 2) || selectedSeries?.carSeriesName">
            <i i-ic:baseline-keyboard-arrow-right />
            <span class="cursor-pointer" text-4 @click="changeStep(2)">{{ selectedModel?.carModelName || '选择车型' }}</span>
          </template>
        </template>
        <n-scrollbar class="transform">
          <div
            v-if="(!initStatus && state !== LOAD_STATUS.LOADING) || state === LOAD_STATUS.ERROR"
            class="py-lg flex flex-col items-center"
          >
            <p class="text-center mb-3 text-gray-4">
              加载失败
            </p>
            <button class="px-3 py-1 rounded-md bg-blue-5 text-white" @click="initHandle">
              重新加载
            </button>
          </div>
          <div v-if="(state === LOAD_STATUS.LOADING)" class="py-5 flex items-center justify-center">
            <van-loading size="16" mr-2 />
            <span ml-2>加载中...</span>
          </div>
          <template v-else>
            <Brand
              v-show="(currentStep === 0)"
              :brand-id="selectedBrand?.id"
              :lazy="props.lazy"
              @select="selectBrand"
            />
            <Series
              v-show="(currentStep === 1 && selectedBrand?.id)"
              :brand-id="selectedBrand?.id"
              :series-id="selectedSeries?.id"
              :lazy="props.lazy"
              @select="selectSeries"
            />
            <Model
              v-show="(currentStep === 2 && selectedSeries?.id)"
              :series-id="selectedSeries?.id"
              :model-id="selectedModel?.id"
              :lazy="props.lazy"
              :can-customize="canCustomize"
              :customize-model-name="customizeModelName"
              @select="selectModel"
            />
          </template>
        </n-scrollbar>
      </n-drawer-content>
    </n-drawer>
  </div>
</template>

<style lang="scss" scoped>
.n-drawer-content:deep(.n-drawer-body-content-wrapper) {
  --uno: p-0 pl-1 pb-4 md:pr-3;
}
</style>
